import { Component, Element, Host, Prop, Watch, h, Listen } from '@stencil/core';
import { isMagnoliaEditingMode } from '../../helper';

interface UiCmsContainerGroupBody {
  columns: 1 | 2 | 3 | 4;
  distribution: '6_6' | '8_4' | '4_8'; // only for 2 columns
  isHeightEqualized?: boolean;
  centerContents?: boolean;
  invertMobileOrder?: boolean;
}

@Component({
  tag: 'ui-cms-container-group',
  styleUrl: 'ui-cms-container-group.scss',
})
export class UiCmsContainerGroup {
  @Element() el: HTMLUiCmsContainerGroupElement;

  @Prop({ mutable: true }) body: UiCmsContainerGroupBody = {} as any;

  @Prop({ attribute: 'body' }) utilityBody: string;

  @Watch('utilityBody')
  utilityBodyChangeHandler(newValue: string) {
    this.body = newValue ? JSON.parse(newValue) : this.body;
  }

  @Listen('resize', { target: 'window' })
  handleResize(): void {
    this.isMobile = window.innerWidth < 768;
    if (this.body.invertMobileOrder) this.changeContainerOrder(this.isMobile);
  }

  private isMobile = window.innerWidth < 768;

  componentWillLoad() {
    this.utilityBodyChangeHandler(this.utilityBody);
    if (this.body.invertMobileOrder) this.changeContainerOrder(this.isMobile);
  }

  private changeContainerOrder(revers: boolean): void {
    const containers = Array.from(this.el.querySelectorAll('ui-cms-container'));
    if (revers) {
      containers.reverse();
    }
    containers.forEach((child: HTMLElement, index: number) => {
      // eslint-disable-next-line no-param-reassign
      child.style.order = `${index + 1}`;
    });
  }

  componentDidLoad() {
    if (isMagnoliaEditingMode()) {
      this.reorderMgnlEditorBars();
    }
  }

  /**
   * Magnolia editor bars have to be reordered in order to be displayed correctly.
   * One reason for this is that the .container-group adds a flexbox layout to the children.
   * Since the editor bars are rendered as siblings of the containers, they have to be moved inside the container-group.
   *
   * A more sustainable solution is described in the following ticket: https://jira.apps.union-investment.de/browse/PB2B-14630
   */
  private reorderMgnlEditorBars(): void {
    let count = 0;
    const checkForEditorBars = () => {
      if (this.el.querySelector('.mgnlEditorBar.start')) {
        clearInterval(intervalID);
        import('./reorder-mgnl-editor-bars').then((module) => {
          module.reorderMgnlEditorBars(this.el);
        });
      }
      count += 1;
      if (count > 10) {
        clearInterval(intervalID);
      }
    };
    const intervalID = setInterval(checkForEditorBars, 200);
  }

  render() {
    return (
      <Host>
        <div
          class={{
            'container-group': true,
            [`container-group--${this.body.columns}`]: true,
            [`column-distribution--${this.body.distribution || '6_6'}`]: this.body.columns === 2,
            'container-group--height-equalized': this.body.isHeightEqualized,
            'container-group--center-content': this.body.centerContents,
            'container-group--mobile-reverse': this.body.invertMobileOrder,
          }}
        >
          <slot></slot>
        </div>
      </Host>
    );
  }
}
